import { useContext, useEffect, useState } from 'react'
import { Participant, Room } from 'twilio-video'

import { AppAudioContext } from '../../../contexts/app_audio_context'
import { useCallManager } from '../../../contexts/call_manager_context'
import { removeItemFromArray } from '../../../reduxes/util/reducers'
import { CS } from '../call_manager/connection_status_reducer'

export const useRemoteParticipants = (
  room: Room | undefined,
): Participant[] | undefined => {
  const [participants, setParticipants] = useState<Participant[] | undefined>()

  const { playJoinedCallSound, playLeftCallSound } = useContext(AppAudioContext)

  const {
    activeCall: { connectionStatus, prevConnectionStatus },
  } = useCallManager()

  useEffect(() => {
    if (room) {
      const p = Array.from(room.participants.values())
      if (p.length) {
        setParticipants(p)
      }
    }
  }, [room])

  useEffect(() => {
    if (room) {
      const participantConnected = (participant: Participant) => {
        playJoinedCallSound()
        setParticipants((prevState: Participant[] | undefined) => [
          ...(prevState || []),
          participant,
        ])
      }

      room.on('participantConnected', participantConnected)

      return () => {
        room.off('participantConnected', participantConnected)
      }
    }
  }, [room, playJoinedCallSound])

  useEffect(() => {
    if (room) {
      const participantDisconnected = (participant: Participant) => {
        playLeftCallSound()
        setParticipants((prevState: Participant[] | undefined) => {
          if (!prevState) {
            return undefined
          }
          const index = prevState.indexOf(participant)
          return removeItemFromArray(prevState, index)
        })
      }

      room.on('participantDisconnected', participantDisconnected)

      return () => {
        room.off('participantDisconnected', participantDisconnected)
      }
    }
  }, [room, playLeftCallSound])

  useEffect(() => {
    if (
      (prevConnectionStatus === CS.ROOM_CONNECTED ||
        prevConnectionStatus === CS.ROOM_RECONNECTING) &&
      connectionStatus === CS.CALL_CONNECTED
    ) {
      if (room) {
        room.participants.forEach((participant: Participant) => {
          participant.removeAllListeners()
        })
        setParticipants(undefined)
      }
    }
  }, [connectionStatus, prevConnectionStatus, room])

  return participants
}
